<script setup>
import { ref, reactive, computed, watch } from 'vue'
import api from '@/services/api'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  modo: { type: String, default: 'preenchimento' },
  fluxo: { type: String, default: 'parent-driven' },
  processo: { type: String, default: '' },
  solicitacao: { type: Object, default: null },
  solicitacaoEdicao: { type: Object, default: null },
  gruposAprovacao: { type: Array, default: () => [] },
  chavePermissao: { type: String, default: '' },
  loading: { type: Boolean, default: false }
})

const emit = defineEmits([
  'update:modelValue',
  'confirmar-preenchimento',
  'solicitar-analise'
])

// --- Computed para v-model ---
const modelValueComputed = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

// --- Estado do formulário ---
const form = reactive({
  lstProcessoAprovacao: props.processo || '',
  resumo: '',
  idGrupoAcessoAprovador: null,
  idUsuarioAprovador: null,
  opcoesAprovacao: [] // { status, decisao, labelBotao }
})

// --- Opções de análise (decisões customizadas) ---
const adicionarOpcao = () => {
  form.opcoesAprovacao.push({ status: 'APROVAR', decisao: '', labelBotao: '' })
}

const removerOpcao = (idx) => {
  form.opcoesAprovacao.splice(idx, 1)
}

const aprovadoresDisponiveis = ref([])
const loadingAprovadores = ref(false)
const errors = reactive({})

// --- Classe visual derivada do TEXTO do status ---
const obterClasseStatus = (textoStatus) => {
  const texto = String(textoStatus || '').toUpperCase()
  if (texto.includes('REPROV') || texto.includes('CANCEL') || texto.includes('ENCERR')) return 'badge-ghost-danger'
  if (texto.includes('APROV') || texto.includes('CONCLU')) return 'badge-ghost-success'
  if (texto.includes('PENDEN')) return 'badge-ghost-warning'
  return 'badge-ghost-secondary'
}

// --- Computed ---
const tituloModal = computed(() => {
  if (props.modo === 'visualizacao') return 'Detalhes da Solicitação'
  if (props.fluxo === 'parent-driven') return 'Preencher Solicitação de Aprovação'
  return 'Nova Solicitação de Aprovação'
})

const textoBotaoConfirmar = computed(() => {
  if (props.fluxo === 'parent-driven') return 'Preencher Solicitação'
  return 'Solicitar Análise'
})

const isVisualizacao = computed(() => props.modo === 'visualizacao')

// --- Carregar aprovadores (padrão PermissoesConsulta) ---
// Com grupo selecionado: usuários do grupo.
// Sem grupo: todos os usuários que possuem a permissão de aprovação do processo.
const carregarAprovadores = async (grupoId) => {
  loadingAprovadores.value = true
  try {
    let response
    if (grupoId) {
      response = await api.get(`/PermissoesConsulta/grupos/${grupoId}/usuarios`)
    } else if (props.chavePermissao) {
      response = await api.get('/PermissoesConsulta/usuarios', {
        params: { chave: props.chavePermissao }
      })
    } else {
      aprovadoresDisponiveis.value = []
      return
    }
    aprovadoresDisponiveis.value = Array.isArray(response.data) ? response.data : []
  } catch (error) {
    console.error('Erro ao carregar aprovadores:', error)
    aprovadoresDisponiveis.value = []
  } finally {
    loadingAprovadores.value = false
  }
}

// --- Watch para troca de grupo: limpa o aprovador e recarrega a lista ---
// Se o grupo for limpo, repovoa com todos os aprovadores da permissão.
let _resetando = false
watch(() => form.idGrupoAcessoAprovador, (novoGrupoId) => {
  if (_resetando) return
  form.idUsuarioAprovador = null
  carregarAprovadores(novoGrupoId)
})

// --- Watch para processo prop ---
watch(() => props.processo, (novo) => {
  if (novo) {
    form.lstProcessoAprovacao = novo
  }
}, { immediate: true })

// --- Validação ---
const validar = () => {
  Object.keys(errors).forEach(key => delete errors[key])

  if (!form.lstProcessoAprovacao) {
    errors.lstProcessoAprovacao = 'O processo de aprovação é obrigatório.'
  }
  if (!form.resumo || form.resumo.trim() === '') {
    errors.resumo = 'Informe um resumo para a solicitação.'
  }
  if (!form.idGrupoAcessoAprovador) {
    errors.idGrupoAcessoAprovador = 'Selecione o grupo aprovador.'
  }

  return Object.keys(errors).length === 0
}

// --- Confirmar ---
const confirmar = () => {
  if (isVisualizacao.value) {
    emit('update:modelValue', false)
    return
  }

  if (!validar()) return

  // Monta o JSON de opções customizadas (apenas as preenchidas)
  const opcoesValidas = form.opcoesAprovacao
    .filter(o => o.decisao && o.labelBotao)
    .map(o => ({ status: o.status, decisao: o.decisao, labelBotao: o.labelBotao }))

  const payload = {
    processo: form.lstProcessoAprovacao,
    resumo: form.resumo.trim(),
    idGrupoAcessoAprovador: form.idGrupoAcessoAprovador,
    idUsuarioAprovador: form.idUsuarioAprovador || null,
    opcoesAprovacaoJson: opcoesValidas.length > 0 ? JSON.stringify(opcoesValidas) : null
  }

  if (props.fluxo === 'parent-driven') {
    emit('confirmar-preenchimento', payload)
  } else {
    emit('solicitar-analise', payload)
  }
}

// --- Fechar ---
const fechar = () => {
  emit('update:modelValue', false)
}

// --- Reset ---
const resetForm = () => {
  _resetando = true
  form.lstProcessoAprovacao = props.processo || ''
  form.resumo = ''
  form.idGrupoAcessoAprovador = null
  form.idUsuarioAprovador = null
  aprovadoresDisponiveis.value = []
  Object.keys(errors).forEach(key => delete errors[key])

  // Modo edição/preenchimento de solicitação existente: pré-preenche os dados
  if (props.solicitacaoEdicao) {
    form.lstProcessoAprovacao = props.solicitacaoEdicao.lstProcessoAprovacao || form.lstProcessoAprovacao
    form.resumo = props.solicitacaoEdicao.resumoSolicitacao || ''
    form.idGrupoAcessoAprovador = props.solicitacaoEdicao.idGrupoAcessoAprovador || null
    form.idUsuarioAprovador = props.solicitacaoEdicao.idUsuarioAprovador || null
  }
  _resetando = false

  // Carrega a lista de aprovadores conforme o grupo pré-preenchido
  // (todos os aprovadores da permissão quando não há grupo)
  carregarAprovadores(form.idGrupoAcessoAprovador)
}

watch(() => props.modelValue, (aberto) => {
  if (aberto && props.modo === 'preenchimento') {
    resetForm()
  }
})
</script>

<template>
  <ModalFormularioV2
    v-model="modelValueComputed"
    :titulo="tituloModal"
    tamanho="lg"
    cor-header="var(--bs-info)"
    :ocultar-confirmar="isVisualizacao"
    @confirmar="confirmar"
    @cancelar="fechar"
  >
    <template #header>
      <h5 class="modal-title-v2 mb-0 d-flex align-items-center gap-2">
        <i class="bi bi-clipboard-check"></i>
        {{ tituloModal }}
      </h5>
    </template>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-info mb-3" role="status"></div>
      <p class="text-body-secondary">Carregando...</p>
    </div>

    <!-- Modo Visualização -->
    <template v-else-if="isVisualizacao && solicitacao">
      <div class="row g-3">
        <div class="col-md-6">
          <div class="border rounded p-2 bg-body-tertiary">
            <small class="text-body-secondary d-block lh-1 mb-1 fw-bold">Processo</small>
            <span class="fw-medium">{{ solicitacao.nomeProcesso || solicitacao.lstProcessoAprovacao || '-' }}</span>
          </div>
        </div>
        <div class="col-md-6">
          <div class="border rounded p-2 bg-body-tertiary">
            <small class="text-body-secondary d-block lh-1 mb-1 fw-bold">Status</small>
            <span class="badge" :class="obterClasseStatus(solicitacao.nomeStatus || solicitacao.lstStatusAprovacao)">
              {{ solicitacao.nomeStatus || solicitacao.lstStatusAprovacao || '-' }}
            </span>
          </div>
        </div>
        <div class="col-12">
          <div class="border rounded p-2 bg-body-tertiary">
            <small class="text-body-secondary d-block lh-1 mb-1 fw-bold">Resumo</small>
            <p class="mb-0">{{ solicitacao.resumoSolicitacao || '-' }}</p>
          </div>
        </div>
        <div class="col-md-6">
          <div class="border rounded p-2 bg-body-tertiary">
            <small class="text-body-secondary d-block lh-1 mb-1 fw-bold">Solicitante</small>
            <span class="fw-medium">{{ solicitacao.nomeUsuarioCriacao || '-' }}</span>
          </div>
        </div>
        <div class="col-md-6">
          <div class="border rounded p-2 bg-body-tertiary">
            <small class="text-body-secondary d-block lh-1 mb-1 fw-bold">Grupo Aprovador</small>
            <span class="fw-medium">{{ solicitacao.nomeGrupoAprovador || '-' }}</span>
          </div>
        </div>
        <div class="col-md-6">
          <div class="border rounded p-2 bg-body-tertiary">
            <small class="text-body-secondary d-block lh-1 mb-1 fw-bold">Aprovador Designado</small>
            <span class="fw-medium">{{ solicitacao.nomeUsuarioAprovador || 'Qualquer aprovador do grupo' }}</span>
          </div>
        </div>
        <div class="col-md-6">
          <div class="border rounded p-2 bg-body-tertiary">
            <small class="text-body-secondary d-block lh-1 mb-1 fw-bold">Parecer</small>
            <span class="fw-medium">{{ solicitacao.observacaoAprovacao || 'Aguardando análise' }}</span>
          </div>
        </div>
      </div>
    </template>

    <!-- Modo Preenchimento -->
    <template v-else>
      <div class="row g-3">
        <!-- Processo (somente leitura; resolvido via Dicionário de Metadados) -->
        <div class="col-md-6">
          <CampoBuscaLista
            id="lstProcessoAprovacao"
            label="Processo de Aprovação"
            v-model="form.lstProcessoAprovacao"
            tabela="WorkflowAprovacaoAnalise"
            required
            disabled
            :error="errors.lstProcessoAprovacao"
          />
        </div>

        <!-- Resumo -->
        <div class="col-12">
          <CampoInput
            id="resumoAprovacao"
            label="Resumo da Solicitação"
            v-model="form.resumo"
            type="textarea"
            :max-length="500"
            required
            :class="{ 'is-invalid': errors.resumo }"
          />
          <div v-if="errors.resumo" class="invalid-feedback d-block">{{ errors.resumo }}</div>
        </div>

        <!-- Grupo Aprovador -->
        <div class="col-md-6">
          <CampoSelect
            id="grupoAprovador"
            label="Grupo Aprovador"
            v-model="form.idGrupoAcessoAprovador"
            :options="gruposAprovacao"
            option-label="nome"
            option-value="idGrupoAcesso"
            required
            :class="{ 'is-invalid': errors.idGrupoAcessoAprovador }"
          />
          <div v-if="errors.idGrupoAcessoAprovador" class="invalid-feedback d-block">{{ errors.idGrupoAcessoAprovador }}</div>
        </div>

        <!-- Aprovador (opcional) -->
        <div class="col-md-6">
          <CampoSelect
            id="aprovadorDesignado"
            label="Aprovador (opcional)"
            v-model="form.idUsuarioAprovador"
            :options="aprovadoresDisponiveis"
            option-label="nome"
            option-value="idUsuario"
            :loading="loadingAprovadores"
          />
          <div class="form-text small text-body-secondary">
            Deixe em branco para qualquer aprovador do grupo.
          </div>
        </div>

        <!-- ============================================================ -->
        <!-- Opções de Análise (decisões customizadas) — opcional          -->
        <!-- ============================================================ -->
        <div class="col-12">
          <div class="border rounded p-2 bg-body-tertiary">
            <div class="d-flex align-items-center gap-2 mb-1">
              <small class="fw-bold text-body-emphasis">
                <i class="bi bi-diagram-3 me-1 text-info"></i> Opções de Análise (opcional)
              </small>
              <button type="button" class="btn btn-outline-info btn-sm py-0 px-2 ms-auto" @click="adicionarOpcao">
                <i class="bi bi-plus-lg me-1"></i> Adicionar opção
              </button>
            </div>
            <div class="form-text small text-body-secondary mb-1">
              Defina as opções que o aprovador verá como botões no modal de análise.
              Deixe vazio para usar o padrão Aprovar/Reprovar.
            </div>

            <div v-for="(opcao, idx) in form.opcoesAprovacao" :key="idx" class="border rounded px-2 py-1 mb-1 small" style="background-color: var(--bs-tertiary-bg);">
              <div class="row g-1 align-items-end">
                <div class="col-md-3">
                  <label class="form-label text-body-secondary" style="font-size: 0.7rem;">Tipo</label>
                  <select class="form-select form-select-sm" v-model="opcao.status">
                    <option value="APROVAR">Aprovar</option>
                    <option value="REPROVAR">Reprovar</option>
                  </select>
                </div>
                <div class="col-md-4">
                  <label class="form-label text-body-secondary" style="font-size: 0.7rem;">Código da Decisão</label>
                  <input type="text" class="form-control form-control-sm" v-model="opcao.decisao" placeholder="ex: APROVAR_COM_RESSALVAS" />
                </div>
                <div class="col-md-4">
                  <label class="form-label text-body-secondary" style="font-size: 0.7rem;">Texto do Botão</label>
                  <input type="text" class="form-control form-control-sm" v-model="opcao.labelBotao" placeholder="ex: Aprovar com Ressalvas" />
                </div>
                <div class="col-md-1 d-flex align-items-end">
                  <button type="button" class="btn btn-outline-danger btn-sm py-0 px-1" @click="removerOpcao(idx)" title="Remover opção">
                    <i class="bi bi-trash3"></i>
                  </button>
                </div>
              </div>
            </div>

            <div v-if="form.opcoesAprovacao.length === 0" class="text-center text-body-secondary small py-2">
              <i class="bi bi-info-circle me-1"></i> Nenhuma opção customizada. O aprovador verá os botões padrão Aprovar/Reprovar.
            </div>
          </div>
        </div>

      </div>
    </template>

    <template #footer>
      <div class="d-flex justify-content-end gap-2 w-100">
        <button type="button" class="btn btn-outline-secondary btn-sm" @click="fechar">
          <i class="bi bi-x-lg me-1"></i> Cancelar
        </button>
        <button
          v-if="!isVisualizacao"
          type="button"
          class="btn btn-info text-white btn-sm"
          @click="confirmar"
          :disabled="loading || loadingAprovadores"
        >
          <i class="bi bi-check-lg me-1"></i> {{ textoBotaoConfirmar }}
        </button>
      </div>
    </template>
  </ModalFormularioV2>
</template>
